import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import { analyzeDiibReconciliation } from "../../../api/muhasebe/diibReconciliationApi";

const STATUS_LABELS = {
  MATCHED: "Eşleşti",
  MISSING_IN_GOKTAS: "Göktaş'ta yok",
  MISSING_IN_YILDIZ: "Yıldız'da yok",
  MISSING_XML: "XML yok",
  INVOICE_NUMBER_MISMATCH: "Fatura no farklı",
  QUANTITY_MISMATCH: "Miktar farklı",
  AMOUNT_MISMATCH: "Tutar farklı",
  DATE_MISMATCH: "Tarih farklı",
  VKN_MISMATCH: "VKN farklı",
  DUPLICATE: "Mükerrer",
  MULTIPLE_MISMATCH: "Birden fazla fark",
};

const getStatusVariant = (status) => {
  if (status === "MATCHED") return "success";
  if (String(status).startsWith("MISSING_")) return "warning";
  if (status === "DUPLICATE") return "secondary";
  return "danger";
};

const formatNumber = (value, digits = 2) => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(number);
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Mutabakat analizi sırasında beklenmeyen bir hata oluştu.";

function DiibReconciliationPage() {
  const formRef = useRef(null);
  const [goktasExcel, setGoktasExcel] = useState(null);
  const [yildizExcel, setYildizExcel] = useState(null);
  const [xmlFiles, setXmlFiles] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const canAnalyze = Boolean(
    goktasExcel && yildizExcel && xmlFiles.length && !loading
  );

  const diagnosticWarnings = useMemo(
    () => result?.diagnostics?.xmlErrors || [],
    [result]
  );

  const resetResult = () => {
    setResult(null);
    setError("");
    setProgress(0);
  };

  const handleXmlFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    setXmlFiles(selected);
    resetResult();
  };

  const clearForm = () => {
    setGoktasExcel(null);
    setYildizExcel(null);
    setXmlFiles([]);
    resetResult();
    formRef.current?.reset();
  };

  const handleAnalyze = async (event) => {
    event.preventDefault();
    if (!canAnalyze) {
      setError("İki Excel dosyasını ve en az bir XML faturasını seçin.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setProgress(0);

    try {
      const response = await analyzeDiibReconciliation({
        goktasExcel,
        yildizExcel,
        xmlFiles,
        onUploadProgress: (eventValue) => {
          if (!eventValue.total) return;
          setProgress(
            Math.min(
              100,
              Math.round((eventValue.loaded * 100) / eventValue.total)
            )
          );
        },
      });
      setResult(response.data);
      setProgress(100);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid px-0">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h2 className="fw-bold mb-1">DİİB Fatura Mutabakatı</h2>
            <div className="text-muted">
              Göktaş ve Yıldız Excel listelerini UBL-TR faturalarıyla karşılaştırır.
            </div>
          </div>
          <Badge bg="primary" className="fs-6 px-3 py-2">
            Faz 1 POC
          </Badge>
        </div>

        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-white fw-semibold py-3">
            Karşılaştırılacak dosyalar
          </Card.Header>
          <Card.Body>
            <Form ref={formRef} onSubmit={handleAnalyze}>
              <Row className="g-3">
                <Col lg={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Göktaş DİİB Excel
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      disabled={loading}
                      onChange={(event) => {
                        setGoktasExcel(event.target.files?.[0] || null);
                        resetResult();
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col lg={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Yıldız satış listesi
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      disabled={loading}
                      onChange={(event) => {
                        setYildizExcel(event.target.files?.[0] || null);
                        resetResult();
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col lg={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      E-Fatura XML dosyaları
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept=".xml,application/xml,text/xml"
                      multiple
                      disabled={loading}
                      onChange={handleXmlFiles}
                    />
                    <Form.Text>{xmlFiles.length} XML seçildi.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {loading || progress > 0 ? (
                <ProgressBar
                  className="mt-4"
                  now={progress}
                  label={`%${progress}`}
                  animated={loading}
                />
              ) : null}

              <div className="d-flex flex-wrap gap-2 mt-4">
                <Button type="submit" disabled={!canAnalyze}>
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Analiz ediliyor
                    </>
                  ) : (
                    "Mutabakatı Başlat"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  disabled={loading}
                  onClick={clearForm}
                >
                  Temizle
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {result?.summary ? (
          <>
            <Row className="g-3 mb-4">
              {[
                ["Toplam fatura", result.summary.total, "primary"],
                ["Eşleşen", result.summary.matched, "success"],
                ["Eksik", result.summary.missing, "warning"],
                ["Uyuşmayan", result.summary.mismatched, "danger"],
              ].map(([label, value, variant]) => (
                <Col sm={6} xl={3} key={label}>
                  <Card className={`border-${variant} shadow-sm h-100`}>
                    <Card.Body>
                      <div className="text-muted small">{label}</div>
                      <div className={`fs-2 fw-bold text-${variant}`}>
                        {value}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {diagnosticWarnings.length ? (
              <Alert variant="warning">
                <div className="fw-semibold mb-1">
                  {diagnosticWarnings.length} XML dosyası okunamadı
                </div>
                {diagnosticWarnings.map((item) => (
                  <div key={item.fileName} className="small">
                    {item.fileName}: {item.message}
                  </div>
                ))}
              </Alert>
            ) : null}

            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white fw-semibold py-3">
                Fatura sonuçları
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Fatura No</th>
                        <th>Durum</th>
                        <th className="text-end">Göktaş KG</th>
                        <th className="text-end">Yıldız KG</th>
                        <th className="text-end">XML KG</th>
                        <th className="text-end">XML Tutar</th>
                        <th className="text-end">XML TL</th>
                        <th>Hatalar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.invoices || []).map((invoice) => (
                        <tr key={invoice.invoiceNumber}>
                          <td className="fw-semibold">
                            {invoice.invoiceNumber}
                          </td>
                          <td>
                            <Badge bg={getStatusVariant(invoice.status)}>
                              {STATUS_LABELS[invoice.status] || invoice.status}
                            </Badge>
                          </td>
                          <td className="text-end">
                            {formatNumber(invoice.goktas?.quantityKg, 3)}
                          </td>
                          <td className="text-end">
                            {formatNumber(invoice.yildiz?.quantityKg, 3)}
                          </td>
                          <td className="text-end">
                            {formatNumber(invoice.xml?.quantityKg, 3)}
                          </td>
                          <td className="text-end">
                            {formatNumber(invoice.xml?.amount)} {invoice.xml?.currency || ""}
                          </td>
                          <td className="text-end">
                            {formatNumber(invoice.xml?.amountTry)}
                          </td>
                          <td style={{ minWidth: 240 }}>
                            {invoice.errors?.length
                              ? invoice.errors.map((item) => item.message).join(" • ")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        ) : null}
      </div>
    </Layout>
  );
}

export default DiibReconciliationPage;
